"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.EMPTY_SEARCH_PAGE = void 0;
exports.isCatalogImageBlock = isCatalogImageBlock;
exports.orderedSearchPageBlockIds = orderedSearchPageBlockIds;
exports.hiddenBlockIds = hiddenBlockIds;
exports.extractDatabaseSearch = extractDatabaseSearch;
exports.extractSearchBlocks = extractSearchBlocks;
const database_identity_1 = require("@streamlain/database-identity");
const cell_text_1 = require("./cell-text");
const text_1 = require("./text");
/** A page without content. */
exports.EMPTY_SEARCH_PAGE = {
    blocks: {},
    children: {},
    elements: {},
    surfaceElements: {},
    members: {},
};
/** Column id of the row title when the database stores none of its own. */
const TITLE_COLUMN_ID = 'title';
const LINK_METADATA_PROPS = {
    'affine:bookmark': ['title', 'url', 'description', 'caption'],
    'affine:embed-iframe': [
        'title',
        'url',
        'description',
        'caption',
        'iframeUrl',
    ],
    'affine:embed-figma': ['title', 'url', 'description', 'caption'],
    'affine:embed-loom': ['title', 'url', 'description', 'caption'],
    'affine:embed-youtube': [
        'title',
        'url',
        'description',
        'caption',
        'creator',
        'creatorUrl',
        'videoId',
    ],
    'affine:embed-github': [
        'title',
        'url',
        'description',
        'caption',
        'repository',
        'githubType',
        'githubId',
        'status',
        'statusReason',
        'assignees',
    ],
};
const TEXT_FLAVOURS = new Set([
    'affine:paragraph',
    'affine:list',
    'affine:code',
    'affine:sticky-note',
    'affine:brush-heading',
]);
const SUMMARY_FLAVOURS = new Set([
    'affine:paragraph',
    'affine:list',
    'affine:code',
]);
function childrenOf(page, blockId) {
    return page.children[blockId] ?? [];
}
function flavourOf(block) {
    const flavour = block?.['sys:flavour'];
    return typeof flavour === 'string' ? flavour : '';
}
function isHidden(blockId, block) {
    return (block['deleted'] === true ||
        block['prop:hidden'] === true ||
        (flavourOf(block) === 'affine:database' &&
            (0, database_identity_1.isHiddenSystemManagedDatabase)({
                id: blockId,
                managedBy: block['prop:managedBy'] ?? block['managedBy'],
                visible: block['prop:visible'] ?? block['visible'],
            })));
}
/** A stored image block that is catalogue art; see isCatalogImage. */
function isCatalogImageBlock(block) {
    return (flavourOf(block) === 'affine:image' &&
        (0, database_identity_1.isCatalogImage)({
            catalogSourceId: block['prop:catalogSourceId'],
            sourceId: block['prop:sourceId'],
        }));
}
function nonEmpty(values) {
    return values.filter(value => value.length > 0);
}
/** Blocks in page order: only the tree under the page block. */
function orderedSearchPageBlockIds(page) {
    const blocksJson = page.blocks;
    const ordered = [];
    const seen = new Set();
    const visit = (blockId) => {
        if (seen.has(blockId) || !blocksJson[blockId])
            return;
        seen.add(blockId);
        ordered.push(blockId);
        for (const child of childrenOf(page, blockId))
            visit(child);
    };
    for (const [blockId, block] of Object.entries(blocksJson)) {
        if (flavourOf(block) === 'affine:page')
            visit(blockId);
    }
    return ordered;
}
/** Deleted and hidden blocks and the blocks under them. */
function hiddenBlockIds(page) {
    const hidden = new Set();
    const hide = (blockId) => {
        if (hidden.has(blockId))
            return;
        hidden.add(blockId);
        for (const child of childrenOf(page, blockId))
            hide(child);
    };
    for (const [blockId, block] of Object.entries(page.blocks)) {
        if (isHidden(blockId, block))
            hide(blockId);
    }
    return hidden;
}
function tableRows(block) {
    const ordered = (kind) => {
        const prefix = `prop:${kind}.`;
        const suffix = '.order';
        return Object.entries(block)
            .filter(([key]) => key.startsWith(prefix) && key.endsWith(suffix))
            .map(([key, order]) => ({
            id: key.slice(prefix.length, -suffix.length),
            order: String(order),
        }))
            .sort((a, b) => (a.order < b.order ? -1 : a.order > b.order ? 1 : 0))
            .map(entry => entry.id);
    };
    const columns = ordered('columns');
    return ordered('rows').map(row => columns.map(column => (0, text_1.plainText)(block[`prop:cells.${row}:${column}.text`])));
}
function linkMetadataFields(block, props) {
    const value = (prop) => {
        if (prop === 'repository') {
            return nonEmpty([value('owner'), value('repo')]).join('/');
        }
        const raw = block[`prop:${prop}`];
        return Array.isArray(raw)
            ? nonEmpty(raw.map(item => (typeof item === 'string' ? item.trim() : ''))).join(', ')
            : (0, text_1.plainText)(raw);
    };
    return namedFields(props.map(prop => [prop, value(prop)]));
}
/** Named texts without the empty ones. */
function namedFields(entries) {
    return Object.fromEntries(entries.filter(([, text]) => text.length > 0));
}
function surfaceElements(page, surfaceId) {
    return (page.surfaceElements[surfaceId] ?? [])
        .map(id => ({ id, record: page.elements[id] }))
        .filter(({ record }) => record && typeof record === 'object')
        .map(({ id, record }) => ({
        id,
        type: typeof record.type === 'string' ? record.type : '',
        record,
    }))
        .filter(element => element.type);
}
function surfaceBlocks(page, surfaceId) {
    const elements = surfaceElements(page, surfaceId);
    const textById = new Map();
    const groupTitleById = new Map();
    const mindmapParentById = new Map();
    const mindmapIdByNode = new Map();
    for (const { id, type, record } of elements) {
        const text = (0, text_1.plainText)(record.text);
        if (text)
            textById.set(id, text);
        if (type === 'group' && typeof record.title === 'string') {
            const title = (0, text_1.plainText)(record.title);
            if (title)
                for (const memberId of page.members[id] ?? [])
                    groupTitleById.set(memberId, title);
        }
        if (type === 'mindmap') {
            // A mind map keeps its node layout as a field.
            const nodes = record.children ??
                {};
            for (const nodeId of Object.keys(nodes)) {
                mindmapParentById.set(nodeId, nodes[nodeId]?.parent);
                mindmapIdByNode.set(nodeId, id);
            }
        }
    }
    const mindmapPath = (nodeId) => {
        if (!mindmapParentById.has(nodeId))
            return undefined;
        const path = [];
        const seen = new Set();
        let parent = mindmapParentById.get(nodeId);
        while (parent && !seen.has(parent)) {
            seen.add(parent);
            const text = textById.get(parent);
            if (text)
                path.unshift(text);
            parent = mindmapParentById.get(parent);
        }
        return path;
    };
    const blocks = [];
    for (const { id, type, record } of elements) {
        const content = type === 'group'
            ? nonEmpty([(0, text_1.plainText)(record.title)])
            : nonEmpty([textById.get(id) ?? '']);
        if (content.length === 0)
            continue;
        const endpointId = (end) => {
            const endId = end?.id;
            return typeof endId === 'string' && endId ? endId : undefined;
        };
        const connector = type === 'connector'
            ? {
                sourceId: endpointId(record.source),
                targetId: endpointId(record.target),
            }
            : undefined;
        if (connector) {
            content.push(...nonEmpty([connector.sourceId, connector.targetId].map(endId => endId ? (textById.get(endId) ?? '') : '')));
        }
        const groupTitle = groupTitleById.get(id);
        const path = mindmapPath(id);
        const mindmapId = mindmapIdByNode.get(id);
        blocks.push({
            blockId: id,
            flavour: `edgeless:${type}`,
            content,
            parentBlockId: surfaceId,
            parentFlavour: 'affine:surface',
            fields: namedFields([[type === 'group' ? 'title' : 'text', content[0]]]),
            additional: {
                // Surface elements exist only on the canvas.
                displayMode: 'edgeless',
                ...(groupTitle && { groupTitle }),
                ...(path && { mindmapPath: path }),
                ...(mindmapId && { mindmapId }),
                ...(connector && { connector }),
            },
        });
    }
    return blocks;
}
function databaseColumns(block) {
    const columns = Array.isArray(block['prop:columns'])
        ? block['prop:columns'].filter((column) => !!column &&
            typeof column === 'object' &&
            typeof column.id === 'string' &&
            typeof column.type === 'string')
        : [];
    return columns.some(column => column.type === 'title')
        ? columns
        : [{ id: TITLE_COLUMN_ID, type: 'title' }, ...columns];
}
/**
 * Search text of one database block: its title and option names (`texts`),
 * the attachment blob ids and the text of every cell of the rows passed. Row
 * titles missing from `databaseRows` come from the row blocks of `page`.
 */
function extractDatabaseSearch(databaseId, block, databaseRows, page = exports.EMPTY_SEARCH_PAGE) {
    const columns = databaseColumns(block);
    const titleColumn = columns.find(column => column.type === 'title');
    const rows = databaseRows ?? {};
    const rowIds = [
        ...new Set([...childrenOf(page, databaseId), ...Object.keys(rows)]),
    ];
    const valueOf = (rowId, column) => column === titleColumn && !rows[rowId]?.[column.id]
        ? page.blocks[rowId]?.['prop:text']
        : (0, database_identity_1.readDatabaseRowStoredCellValue)(column.id, rows[rowId]?.[column.id]);
    const rowLabels = Object.fromEntries(rowIds.map(rowId => [
        rowId,
        (0, cell_text_1.cellToText)(titleColumn, valueOf(rowId, titleColumn)),
    ]));
    const cells = [];
    const blobs = [];
    for (const rowId of rowIds) {
        for (const column of columns) {
            const value = valueOf(rowId, column);
            if (column.type === 'attachment' && value && typeof value === 'object') {
                blobs.push(...Object.keys(value));
            }
            const relationTarget = column.data?.targetDatabaseId;
            const text = (0, cell_text_1.cellToText)(column, value, {
                relationLabels: relationTarget === databaseId ||
                    relationTarget === block['prop:databaseId']
                    ? rowLabels
                    : undefined,
            });
            if (!text)
                continue;
            cells.push({
                databaseBlockId: databaseId,
                rowId,
                columnId: column.id,
                columnType: column.type,
                rowLabel: rowLabels[rowId] ?? '',
                text,
            });
        }
    }
    return {
        texts: nonEmpty([
            (0, text_1.plainText)(block['prop:title']),
            ...columns.flatMap(column => [...(0, cell_text_1.columnOptionLabels)(column).values()]),
        ]),
        blobs,
        cells,
    };
}
/**
 * Search text of one doc, read from its page as plain JSON. The same
 * rules serve the server index, the browser and the desktop app.
 */
function extractSearchBlocks(page, options = {}) {
    let summaryBudget = options.maxSummaryLength ?? 1000;
    let summary = '';
    let title = '';
    const blocks = [];
    const cells = [];
    const blocksJson = page.blocks;
    const hidden = hiddenBlockIds(page);
    const parentById = new Map();
    for (const [blockId, children] of Object.entries(page.children)) {
        for (const child of children)
            parentById.set(child, blockId);
    }
    const nearest = (blockId, flavour) => {
        for (let current = parentById.get(blockId); current; current = parentById.get(current)) {
            if (flavourOf(blocksJson[current]) === flavour)
                return current;
        }
        return undefined;
    };
    for (const blockId of orderedSearchPageBlockIds(page)) {
        if (hidden.has(blockId))
            continue;
        const block = blocksJson[blockId];
        const flavour = flavourOf(block);
        const parentBlockId = parentById.get(blockId);
        const parentFlavour = parentBlockId
            ? flavourOf(blocksJson[parentBlockId])
            : undefined;
        const noteBlockId = nearest(blockId, 'affine:note');
        const frameBlockId = nearest(blockId, 'affine:frame');
        const displayMode = noteBlockId
            ? blocksJson[noteBlockId]['prop:displayMode']
            : undefined;
        const frameTitle = frameBlockId
            ? (0, text_1.plainText)(blocksJson[frameBlockId]['prop:title'])
            : '';
        const base = {
            blockId,
            flavour,
            ...(parentBlockId && { parentBlockId, parentFlavour }),
        };
        const additional = {
            displayMode: displayMode === 'both' || displayMode === 'page' ? 'page' : 'edgeless',
            ...(noteBlockId && { noteBlockId }),
            ...(frameTitle && { frameTitle }),
        };
        const push = (fields) => blocks.push({
            ...base,
            ...fields,
            additional: { ...additional, ...fields.additional },
        });
        if (flavour === 'affine:page') {
            title = (0, text_1.plainText)(block['prop:title']);
            push({ content: nonEmpty([title]) });
        }
        else if (TEXT_FLAVOURS.has(flavour)) {
            const { text, refDocIds } = (0, text_1.searchText)(block['prop:text']);
            push({
                content: nonEmpty([text]),
                ...(flavour === 'affine:sticky-note' && {
                    fields: namedFields([
                        ['assigneeId', (0, text_1.plainText)(block['prop:assigneeId'])],
                    ]),
                }),
                ...(refDocIds.length > 0 && { refDocId: refDocIds }),
                ...(parentFlavour === 'affine:database' && {
                    additional: {
                        databaseName: (0, text_1.plainText)(blocksJson[parentBlockId]['prop:title']),
                    },
                }),
            });
            if (SUMMARY_FLAVOURS.has(flavour) && summaryBudget > 0 && text) {
                summary += text;
                summaryBudget -= text.length;
            }
        }
        else if (flavour === 'affine:embed-linked-doc' ||
            flavour === 'affine:embed-synced-doc') {
            const pageId = block['prop:pageId'];
            push({
                content: [],
                ...(typeof pageId === 'string' && { refDocId: [pageId] }),
            });
        }
        else if (isCatalogImageBlock(block)) {
            continue;
        }
        else if (flavour === 'affine:attachment' || flavour === 'affine:image') {
            const blobId = block['prop:sourceId'];
            const fields = namedFields([
                ['name', (0, text_1.plainText)(block['prop:name'])],
                ['caption', (0, text_1.plainText)(block['prop:caption'])],
                ['sourceCaption', (0, text_1.plainText)(block['prop:sourceCaption'])],
            ]);
            push({
                content: Object.values(fields),
                fields,
                ...(typeof blobId === 'string' && blobId && { blob: [blobId] }),
            });
        }
        else if (flavour === 'affine:frame') {
            const fields = namedFields([['title', (0, text_1.plainText)(block['prop:title'])]]);
            push({ content: Object.values(fields), fields });
        }
        else if (flavour === 'affine:latex') {
            push({ content: nonEmpty([(0, text_1.plainText)(block['prop:latex'])]) });
        }
        else if (flavour === 'affine:table') {
            const table = tableRows(block);
            push({ content: nonEmpty(table.flat()), table });
        }
        else if (flavour in LINK_METADATA_PROPS) {
            const fields = linkMetadataFields(block, LINK_METADATA_PROPS[flavour]);
            push({ content: Object.values(fields), fields });
        }
        else if (flavour === 'affine:surface') {
            blocks.push(...surfaceBlocks(page, blockId));
        }
        else if (flavour === 'affine:database') {
            const database = extractDatabaseSearch(blockId, block, options.databaseRows?.[blockId], page);
            cells.push(...database.cells);
            push({
                content: database.texts,
                fields: namedFields([['title', (0, text_1.plainText)(block['prop:title'])]]),
                ...(database.blobs.length > 0 && { blob: database.blobs }),
                additional: { databaseName: (0, text_1.plainText)(block['prop:title']) },
            });
        }
    }
    return { title, summary, blocks, cells };
}
//# sourceMappingURL=extract.js.map